import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlayerMedical = () => {
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [medicalStatus, setMedicalStatus] = useState(null);
  const [injuries, setInjuries] = useState([]);
  const [recoveryPlans, setRecoveryPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMedicalData();
  }, []);

  const fetchMedicalData = async () => {
    try {
      const athletesRes = await axios.get(`${API}/athletes`);
      if (athletesRes.data.length > 0) {
        const athleteData = athletesRes.data[0];
        setAthlete(athleteData);
        
        const [statusRes, injuriesRes, plansRes] = await Promise.all([
          axios.get(`${API}/medical/athlete/${athleteData.id}`),
          axios.get(`${API}/injuries/athlete/${athleteData.id}`),
          axios.get(`${API}/recovery-plans/athlete/${athleteData.id}`)
        ]);
        
        setMedicalStatus(statusRes.data);
        setInjuries(injuriesRes.data);
        setRecoveryPlans(plansRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch medical data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      healthy: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      injured: 'bg-red-100 text-red-800 border-red-200',
      recovering: 'bg-amber-100 text-amber-800 border-amber-200',
      restricted: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[status] || colors.healthy;
  };

  const getInjuryStatusBadge = (status) => {
    const colors = {
      active: 'bg-red-100 text-red-800 border-red-200',
      recovering: 'bg-amber-100 text-amber-800 border-amber-200',
      closed: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
    return colors[status] || colors.active;
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Medical & Physical Condition
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">View your medical status and recovery information</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athlete ? (
          <Card data-testid="no-athlete-message">
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">Profile not linked. Contact administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card data-testid="status-card">
              <CardHeader>
                <CardTitle>Current Medical Status</CardTitle>
                <CardDescription>Your current physical condition</CardDescription>
              </CardHeader>
              <CardContent>
                {medicalStatus ? (
                  <Badge className={`${getStatusBadge(medicalStatus.status)} text-lg px-4 py-2`} data-testid="medical-status">
                    {medicalStatus.status.charAt(0).toUpperCase() + medicalStatus.status.slice(1)}
                  </Badge>
                ) : (
                  <p className="text-gray-500 dark:text-gray-500">No status recorded</p>
                )}
              </CardContent>
            </Card>

            <Tabs defaultValue="injuries">
              <TabsList>
                <TabsTrigger value="injuries" data-testid="injuries-tab">Injury History</TabsTrigger>
                <TabsTrigger value="recovery" data-testid="recovery-tab">Recovery Plans</TabsTrigger>
              </TabsList>

              <TabsContent value="injuries" className="space-y-4">
                {injuries.length === 0 ? (
                  <Card><CardContent className="py-8 text-center"><p className="text-gray-500 dark:text-gray-500">No injuries recorded</p></CardContent></Card>
                ) : (
                  <div className="space-y-3" data-testid="injuries-list">
                    {injuries.map((injury) => (
                      <Card key={injury.id} data-testid={`injury-${injury.id}`}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg">{injury.injury_type}</CardTitle>
                              <CardDescription>{injury.body_part}</CardDescription>
                            </div>
                            <Badge className={getInjuryStatusBadge(injury.status)} data-testid={`injury-status-${injury.id}`}>
                              {injury.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500 dark:text-gray-500">Start Date</p>
                              <p className="font-medium">{new Date(injury.start_date).toLocaleDateString()}</p>
                            </div>
                            {injury.estimated_return_date && (
                              <div>
                                <p className="text-gray-500 dark:text-gray-500">Est. Return</p>
                                <p className="font-medium">{new Date(injury.estimated_return_date).toLocaleDateString()}</p>
                              </div>
                            )}
                          </div>
                          {injury.note && (
                            <div className="pt-2 border-t">
                              <p className="text-sm text-gray-700 dark:text-gray-300">{injury.note}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="recovery" className="space-y-4">
                {recoveryPlans.length === 0 ? (
                  <Card><CardContent className="py-8 text-center"><p className="text-gray-500 dark:text-gray-500">No recovery plans</p></CardContent></Card>
                ) : (
                  <div className="space-y-3" data-testid="recovery-list">
                    {recoveryPlans.map((plan) => (
                      <Card key={plan.id} data-testid={`recovery-${plan.id}`}>
                        <CardHeader>
                          <CardTitle className="text-lg">Recovery Plan</CardTitle>
                          <CardDescription>Created {new Date(plan.created_at).toLocaleDateString()}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan Description</p>
                            <p className="text-sm text-gray-600 dark:text-gray-500">{plan.plan_description}</p>
                          </div>
                          {plan.restrictions && (
                            <div className="bg-amber-50 rounded-lg p-3">
                              <p className="text-sm font-medium text-amber-900 mb-1">⚠️ Restrictions</p>
                              <p className="text-sm text-amber-800">{plan.restrictions}</p>
                            </div>
                          )}
                          {plan.progress_notes && (
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-sm font-medium text-blue-900 mb-1">📋 Progress Notes</p>
                              <p className="text-sm text-blue-800">{plan.progress_notes}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayerMedical;
